import { useCallback, useEffect, useState } from 'react';
import { format, startOfMonth } from 'date-fns';

import {
  IBenchmarkType,
  IDateRange,
  IDepartment,
  IOrganisation,
  OptionType,
} from '../types/filters';
import FiltersContext, { DisplayType } from '../context/FiltersContext';
import { IFilter } from '../types/context';
import { getDateFilterWindow } from '../../utilities/common/date';
import { DateFilterOptions } from '../constants/HeadingFilter';
import { SelectedBenchmark } from '../constants/filter/filter';
import { ISO_DATE_FORMAT } from '../constants/date';

const reset = {
  dateRange: {
    fromDate: format(startOfMonth(new Date()), ISO_DATE_FORMAT),
    toDate: format(new Date(), ISO_DATE_FORMAT),
  },
  benchmarkType: SelectedBenchmark.GLOBAL,
  ageFilter: [],
  regionFilter: [],
  genderFilter: [],
  selectedDepartments: new Map(),
  selectedOrganisations: new Map(),
} satisfies IFilter;

export default function (props: any) {
  const [dateRange, setDateRange] = useState<IDateRange>({
    ...reset.dateRange,
  });

  const [displayType, setDisplayType] = useState<DisplayType>('absolute');
  const [selectedDate, setSelectedDate] = useState(DateFilterOptions[0]);

  const [benchmarkType, setBenchmarkType] = useState<IBenchmarkType>(
    reset.benchmarkType,
  );

  const [selectedDepartments, setSelectedDepartments] = useState<
    Map<number, boolean>
  >(new Map());
  const [selectedOrganisations, setSelectedOrganisations] = useState<
    Map<number, boolean>
  >(new Map());
  const [selectedAllOrg, setSelectedAllOrg] = useState<
    IOrganisation[] | undefined
  >();

  const [genderFilter, setGenderFilter] = useState<string[]>([]);
  const [ageFilter, setAgeFilter] = useState<string[]>([]);
  const [regionFilter, setRegionFilter] = useState<string[]>([]);

  const [appliedFilters, setAppliedFilters] = useState<IFilter>({ ...reset });
  const [regionOptions, setRegionOptions] = useState<OptionType<string>[]>([]);
  const [selectAllChecked, setSelectAllChecked] = useState<{
    [key: string]: boolean;
  }>({
    filter_org: false,
    filter_region: false,
  });

  const getFilters = useCallback(() => appliedFilters, [appliedFilters]);

  return (
    <FiltersContext.Provider
      value={{
        dateRange,
        setDateRange(dateRange) {
          setAppliedFilters((prev) => {
            return {
              ...prev,
              dateRange,
            };
          });

          setDateRange(dateRange);
        },
        setMonthOffset: (newMonthOffset) => {
          const initialDateRange = getDateFilterWindow({
            monthOffset: newMonthOffset,
          });
          setAppliedFilters({
            ...appliedFilters,
            dateRange: initialDateRange,
          });
          setDateRange(initialDateRange);
        },
        selectedDate,
        setSelectedDate,
        displayType,
        setDisplayType,

        benchmarkType,
        setBenchmarkType: (benchmarkType: IBenchmarkType) => {
          setAppliedFilters((prev) => {
            return {
              ...prev,
              benchmarkType,
            };
          });

          setBenchmarkType(benchmarkType);
        },

        selectedDepartments,
        selectedOrganisations,
        selectedAllOrg,
        toggleDepartmentAndChildren: (department: IDepartment) => {
          const newSelectedDepartments = new Map(selectedDepartments);
          const updatedValue = !newSelectedDepartments.get(department.id);

          // Recursively update children: always deselect them
          const updateChildSelection = (children: IDepartment[]) => {
            children.forEach(({ id, children }) => {
              newSelectedDepartments.set(id, false); // Always deselect children
              if (children?.length) {
                updateChildSelection(children);
              }
            });
          };

          // Toggle the parent department
          newSelectedDepartments.set(department.id, updatedValue);

          // If the department has children:
          // - If selecting parent → deselect all children
          // - If deselecting parent → also deselect all children
          if (department.children) {
            updateChildSelection(department.children);
          }

          setSelectedDepartments(newSelectedDepartments);
        },

        toggleAllOrg: (orgs: IOrganisation[]) => {
          setSelectedAllOrg(orgs);
        },
        toggleOrganisations: (organisation: IOrganisation) => {
          const newSelectedOrganisations = new Map(selectedOrganisations);
          const updatedValue = !newSelectedOrganisations.get(organisation.id);
          const toggleOrganisation = (organisation: IOrganisation) => {
            newSelectedOrganisations.set(organisation.id, updatedValue);
          };

          toggleOrganisation(organisation);
          setSelectedOrganisations(newSelectedOrganisations);
        },
        selectOrDeselectAllDepartments: (newList: Map<number, boolean>) => {
          setSelectedDepartments(newList);
        },
        selectOrDeselectAllOrganisations: (newList: Map<number, boolean>) => {
          setSelectedOrganisations(newList);
        },
        resetFilter: () => {
          setSelectedDepartments(new Map());
          setSelectedOrganisations(new Map());
          setGenderFilter([]);
          setAgeFilter([]);
          setRegionFilter([]);
        },
        cancelFilter: () => {
          setSelectedDepartments(appliedFilters.selectedDepartments);
          setSelectedOrganisations(appliedFilters.selectedOrganisations);
          setGenderFilter([...appliedFilters.genderFilter]);
          setAgeFilter([...appliedFilters.ageFilter]);
          setRegionFilter([...appliedFilters.regionFilter]);
        },
        cancelDateFilter: () => {
          setDateRange({
            ...reset.dateRange,
          });
        },
        saveFilter: () => {
          setAppliedFilters({
            benchmarkType,
            dateRange,
            ageFilter,
            regionFilter,
            genderFilter,
            selectedDepartments,
            selectedOrganisations,
          });
        },
        appliedFilters,
        genderFilter,
        setGenderFilter,
        ageFilter,
        setAgeFilter,
        regionFilter,
        regionOptions, // Include regionOptions in the context value
        setRegionOptions,
        setRegionFilter,
        getFilters,
        selectAllChecked,
        setSelectAllChecked,
      }}
    >
      {props.children}
    </FiltersContext.Provider>
  );
}
